/* eslint-disable prefer-destructuring */
import { PunctualityReportCriteria } from '@src/main/modules/reports/shared/domain/report.interface';
import { toEndOfDayUTC, toLocalISODate, toStartOfDayUTC } from '@src/main/shared/utils/dateUtils';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { DataTableFilterMeta } from 'primereact/datatable';

export type PunctualityFilterMeta = DataTableFilterMeta & {
  global: { value: string | null; matchMode: FilterMatchMode };
  carrierCode: {
    operator: FilterOperator;
    constraints: Array<{ value: string | null; matchMode: FilterMatchMode }>;
  };
  flightNumber: {
    operator: FilterOperator;
    constraints: Array<{ value: string | null; matchMode: FilterMatchMode }>;
  };
  departureAirport: {
    operator: FilterOperator;
    constraints: Array<{ value: string | null; matchMode: FilterMatchMode }>;
  };
  arrivalAirport: {
    operator: FilterOperator;
    constraints: Array<{ value: string | null; matchMode: FilterMatchMode }>;
  };
  aircraftRegistration: {
    operator: FilterOperator;
    constraints: Array<{ value: string | null; matchMode: FilterMatchMode }>;
  };
  employeeId: {
    operator: FilterOperator;
    constraints: Array<{ value: string | null; matchMode: FilterMatchMode }>;
  };
  flightDate: {
    operator: FilterOperator;
    constraints: Array<{ value: [Date | null, Date | null]; matchMode: FilterMatchMode.BETWEEN }>;
  };
  updatedAt: {
    operator: FilterOperator;
    constraints: Array<{
      value: [Date | null, Date | null] | null;
      matchMode: FilterMatchMode.BETWEEN;
    }>;
  };
};

export const mapPunctualityFiltersToCriteria = (
  filters: PunctualityFilterMeta,
  emptyCriteria: PunctualityReportCriteria,
): PunctualityReportCriteria => {
  const criteria: PunctualityReportCriteria = { ...emptyCriteria };

  const normalize = (value?: string | null) =>
    typeof value === 'string' ? value.toUpperCase().trim() : value;

  const fieldMap: Record<
    keyof Pick<
      PunctualityFilterMeta,
      | 'carrierCode'
      | 'flightNumber'
      | 'departureAirport'
      | 'arrivalAirport'
      | 'aircraftRegistration'
      | 'employeeId'
    >,
    keyof PunctualityReportCriteria
  > = {
    carrierCode: 'carrierCode',
    flightNumber: 'flightNumber',
    departureAirport: 'airportFrom',
    arrivalAirport: 'airportTo',
    aircraftRegistration: 'aircraftRegistration',
    employeeId: 'employeeId',
  };

  Object.keys(fieldMap).forEach((filterKey) => {
    const criteriaKey = fieldMap[filterKey as keyof typeof fieldMap];
    const filter = filters[filterKey as keyof PunctualityFilterMeta];
    const value = filter && 'constraints' in filter ? filter.constraints?.[0]?.value : null;
    if (value) {
      (criteria[criteriaKey] as string) = normalize(value) as string;
    }
  });

  const flightDateRange = filters.flightDate?.constraints?.[0]?.value;
  if (flightDateRange) {
    const [start, end] = flightDateRange;
    if (start) criteria.flightSince = toLocalISODate(start);
    if (end) criteria.flightUntil = toLocalISODate(end);
  }

  const updatedAtRange = filters.updatedAt?.constraints?.[0]?.value;
  if (updatedAtRange && (updatedAtRange[0] || updatedAtRange[1])) {
    const [start, end] = updatedAtRange;
    if (start) criteria.formUpdatedSince = toStartOfDayUTC(start);
    if (end) criteria.formUpdatedUntil = toEndOfDayUTC(end);
  }

  return criteria;
};
